import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import styles from "./static-link-editor.module.scss";
import { useI18N } from "@src/language/language-context";
import { Input2025 } from "@src/components/atoms/input-2025/input-2025";
import { PillNavigation } from "@src/components/atoms/pill-navigation/pill-navigation";
import {
  ColorSwatchPicker,
  DEFAULT_TREND_LINE_COLOR,
  TREND_LINE_PALETTE,
  pickPaletteHex,
} from "@src/components/atoms/color-swatch-picker/color-swatch-picker";
import { CheckBoxWithText } from "@src/components/atoms/check-box-with-text/check-box-with-text";
import { DashboardEditWidgetDrawerDropdown } from "@src/components/atoms/dashboard-edit-widget-drawer-dropdown/dashboard-edit-widget-drawer-dropdown";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { Button } from "@src/components/atoms/buttons/button/button";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { useSaveWidget } from "../hooks/use-save-widget";
import { Widget } from "@src/types/dashboards";
import { RootState } from "@src/reducers/store";

const cssClassName = "static-link-editor";

const STATIC_LINK_FONT_SIZES = [10, 12, 14, 16, 20] as const;
type StaticAlignment = "left" | "center" | "right";

export interface StaticLinkEditorProps {
  widget: Widget;
  isPendingWidget: boolean;
  onClose: () => void;
}

export const StaticLinkEditor = ({
  widget,
  isPendingWidget,
  onClose,
}: StaticLinkEditorProps) => {
  const i18n = useI18N();

  const dashboards = useSelector(
    (state: RootState) => state.dashboards.dashboards,
  );
  // Published dashboards only; the list already lives in the slice. A saved
  // id that is no longer published is still shown as `{ name: id }` so the
  // user can see and clear it.
  const dashboardOptions = useMemo(
    () =>
      dashboards
        .filter((d) => d.status === "published")
        .map((d) => ({ name: d.name, source_id: d.id })),
    [dashboards],
  );

  const [content, setContent] = useState<string>(
    () => widget.presentation?.content ?? "",
  );
  const [fontSize, setFontSize] = useState<number>(
    () => widget.presentation?.font_size ?? 16,
  );
  const [alignment, setAlignment] = useState<StaticAlignment>(
    () => widget.presentation?.alignment ?? "left",
  );
  const [color, setColor] = useState<string>(
    () =>
      widget.presentation?.color ??
      (isPendingWidget ? pickPaletteHex() : DEFAULT_TREND_LINE_COLOR),
  );
  const [transparent, setTransparent] = useState<boolean | undefined | null>(
    () => (isPendingWidget ? true : widget.presentation?.transparent),
  );
  const [dashboardId, setDashboardId] = useState<string | null>(() =>
    typeof widget.presentation?.dashboard_id === "string"
      ? widget.presentation.dashboard_id
      : null,
  );

  const texts = {
    contentLabel: i18n("modules.staticLink.contentLabel"),
    contentPlaceholder: i18n("modules.staticLink.contentPlaceholder"),
    fontSizeLabel: i18n("modules.staticLink.fontSizeLabel"),
    alignmentLabel: i18n("modules.staticLink.alignmentLabel"),
    alignmentLeft: i18n("modules.staticLink.alignmentLeft"),
    alignmentCenter: i18n("modules.staticLink.alignmentCenter"),
    alignmentRight: i18n("modules.staticLink.alignmentRight"),
    fontColorLabel: i18n("modules.staticLink.fontColorLabel"),
    transparentLabel: i18n("modules.staticLink.transparentLabel"),
    dashboardLinkLabel: i18n("modules.staticLink.dashboardLinkLabel"),
    dashboardLinkPlaceholder: i18n(
      "modules.staticLink.dashboardLinkPlaceholder",
    ),
    noDashboards: i18n("modules.staticLink.noDashboards"),
    dashboardLinkRequired: i18n("modules.staticLink.dashboardLinkRequired"),
    saveButton: i18n("modules.staticLink.saveButton"),
    cancelButton: i18n("modules.staticLink.cancelButton"),
  };

  // A link widget without a target is just text — require the dashboard
  // before it can be saved.
  const validate = (): string | null =>
    dashboardId ? null : texts.dashboardLinkRequired;

  const { save, isSaving } = useSaveWidget(widget, isPendingWidget, onClose);

  const handleSave = () => {
    const presentation = {
      ...widget.presentation,
      content,
      font_size: fontSize,
      alignment,
      color,
      transparent: transparent ? true : undefined,
      dashboard_id: dashboardId ?? undefined,
    };
    save(
      {
        ...widget,
        image_url: widget.image_url ?? null,
        presentation,
      },
      { validate, imageFile: null },
    );
  };

  return (
    <div className={styles[`${cssClassName}__outer-wrapper`]}>
      <Input2025
        type="text"
        labelText={texts.contentLabel}
        inputPlaceHolder={texts.contentPlaceholder}
        inputValue={content}
        onChangeFunc={(e) => setContent(e.target.value)}
        inputVariant="dark-background"
      />
      <span className={styles[`${cssClassName}__field-label`]}>
        {texts.fontSizeLabel}
      </span>
      <PillNavigation
        options={STATIC_LINK_FONT_SIZES.map((sz) => ({
          name: String(sz),
          link: String(sz),
          render: true,
        }))}
        border={true}
        activeView={String(fontSize)}
        onChangeCallback={(value) => setFontSize(Number(value))}
      />
      <span className={styles[`${cssClassName}__field-label`]}>
        {texts.alignmentLabel}
      </span>
      <PillNavigation
        options={[
          { name: texts.alignmentLeft, link: "left", render: true },
          { name: texts.alignmentCenter, link: "center", render: true },
          { name: texts.alignmentRight, link: "right", render: true },
        ]}
        border={true}
        activeView={alignment}
        onChangeCallback={(value) => setAlignment(value as StaticAlignment)}
      />
      <div className={styles[`${cssClassName}__field`]}>
        <ColorSwatchPicker
          label={texts.fontColorLabel}
          value={color}
          swatches={TREND_LINE_PALETTE}
          onChange={setColor}
        />
      </div>
      <CheckBoxWithText
        text={texts.transparentLabel}
        checkboxState={transparent}
        handleCheckBoxClick={() => setTransparent((prev) => !prev)}
      />
      <DashboardEditWidgetDrawerDropdown
        options={dashboardOptions}
        selectedOption={
          dashboardId
            ? dashboardOptions.find((o) => o.source_id === dashboardId) ?? {
                name: dashboardId,
                source_id: dashboardId,
              }
            : null
        }
        onSelectOption={(o) => setDashboardId(o?.source_id ?? null)}
        labelText={texts.dashboardLinkLabel}
        noOptionsText={texts.noDashboards}
        placeholderText={texts.dashboardLinkPlaceholder}
        isLoading={false}
      />
      <FlexRowContainer>
        <ButtonWithLoader
          fun={handleSave}
          text={texts.saveButton}
          loading={isSaving}
        />
        <Button
          fun={onClose}
          text={texts.cancelButton}
          variant="secondary"
          disabled={isSaving}
        />
      </FlexRowContainer>
    </div>
  );
};
